import type { ReactNode } from 'react';
import { getPreviewAudioEngine } from '../../engine/previewAudio';
import { formatTimecode, projectDuration } from '../../timeline/ops';
import { useEditorStore } from '../../state/editorStore';
import { IconButton } from '../controls/PanelShell';

function TransportBtn({
  title,
  onClick,
  active,
  children,
  accent,
}: {
  title: string;
  onClick: () => void;
  active?: boolean;
  children: ReactNode;
  accent?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`inline-flex h-8 min-w-8 items-center justify-center px-1.5 text-xs transition-colors ${
        accent
          ? 'bg-accent text-base hover:brightness-110'
          : active
            ? 'bg-raised text-accent'
            : 'text-text-high hover:bg-raised hover:text-accent'
      }`}
    >
      {children}
    </button>
  );
}

/** Bottom-center viewer transport: playhead controls + markers. */
export function ViewerTransport() {
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const togglePlay = useEditorStore((s) => s.togglePlay);
  const stepFrame = useEditorStore((s) => s.stepFrame);
  const goToStart = useEditorStore((s) => s.goToStart);
  const goToEnd = useEditorStore((s) => s.goToEnd);
  const addMarker = useEditorStore((s) => s.addMarker);
  const removeMarker = useEditorStore((s) => s.removeMarker);
  const jumpToMarker = useEditorStore((s) => s.jumpToMarker);
  const setInPoint = useEditorStore((s) => s.setInPoint);
  const setOutPoint = useEditorStore((s) => s.setOutPoint);
  const inPoint = useEditorStore((s) => s.inPoint);
  const outPoint = useEditorStore((s) => s.outPoint);
  const layoutId = useEditorStore((s) => s.layout.id);
  const isLeader = useEditorStore((s) => s.isLeader);
  const connectedTabs = useEditorStore((s) => s.connectedTabs);
  const fps = project.settings.frameRate;
  const duration = projectDuration(project);
  const markers = project.markers ?? [];
  const markerAtPlayhead = markers.find((m) => Math.abs(m.time - playhead) < 1 / fps / 2);

  const kickAudio = (playing: boolean) => {
    const output =
      layoutId === 'primary' || isLeader || connectedTabs.length <= 1;
    getPreviewAudioEngine().sync(project, playhead, playing && output);
  };

  return (
    <div className="flex shrink-0 flex-col border-t border-hairline bg-panel">
      {/* Scrub bar */}
      <div className="px-3 pt-2">
        <input
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={1 / fps}
          value={Math.min(playhead, duration)}
          onChange={(e) => {
            useEditorStore.getState().setPlaying(false);
            useEditorStore.getState().setPlayhead(Number(e.target.value));
          }}
          className="w-full"
          aria-label="Playhead"
        />
      </div>

      <div className="flex items-center justify-center gap-0.5 px-2 pb-2 pt-1">
        <TransportBtn title="Previous marker" onClick={() => jumpToMarker(-1)}>
          ⇤
        </TransportBtn>
        <TransportBtn title="Go to start (Home)" onClick={goToStart}>
          ⏮
        </TransportBtn>
        <TransportBtn title="Previous frame (←)" onClick={() => stepFrame(-1)}>
          ‹
        </TransportBtn>
        <TransportBtn
          title="Play / Pause (Space)"
          onClick={() => {
            const next = !isPlaying;
            togglePlay();
            // Start audio inside the click gesture (browser autoplay policy)
            kickAudio(next);
          }}
          accent
        >
          {isPlaying ? '❚❚' : '▶'}
        </TransportBtn>
        <TransportBtn title="Next frame (→)" onClick={() => stepFrame(1)}>
          ›
        </TransportBtn>
        <TransportBtn title="Go to end" onClick={goToEnd}>
          ⏭
        </TransportBtn>
        <TransportBtn title="Next marker" onClick={() => jumpToMarker(1)}>
          ⇥
        </TransportBtn>

        <div className="mx-1 h-5 w-px bg-hairline" />

        <span className="timecode min-w-[7.5rem] text-center text-xs text-playhead">
          {formatTimecode(playhead, fps)}
        </span>
        <span className="timecode text-2xs text-text-low">/</span>
        <span className="timecode min-w-[7.5rem] text-center text-2xs text-text-low">
          {formatTimecode(duration, fps)}
        </span>

        <div className="mx-1 h-5 w-px bg-hairline" />

        <TransportBtn
          title="Mark In (I)"
          active={inPoint != null && Math.abs(inPoint - playhead) < 1 / fps}
          onClick={() => setInPoint(playhead)}
        >
          {'{'}
        </TransportBtn>
        <TransportBtn
          title="Mark Out (O)"
          active={outPoint != null && Math.abs(outPoint - playhead) < 1 / fps}
          onClick={() => setOutPoint(playhead)}
        >
          {'}'}
        </TransportBtn>
        {markerAtPlayhead ? (
          <TransportBtn
            title="Remove marker at playhead"
            onClick={() => removeMarker(markerAtPlayhead.id)}
          >
            ✕M
          </TransportBtn>
        ) : (
          <TransportBtn title="Add marker (M)" onClick={() => addMarker()}>
            ◆
          </TransportBtn>
        )}
        <IconButton
          title={`${markers.length} marker(s) — click to jump next`}
          onClick={() => jumpToMarker(1)}
        >
          <span className="text-2xs text-text-low">{markers.length}</span>
        </IconButton>
      </div>
    </div>
  );
}
